import React, { useState, useEffect } from "react";
import { DateRange } from "react-date-range";
import { enUS } from "date-fns/locale";
import "react-date-range/dist/styles.css"; 
import { getAllProperties, propertiesToArray } from "../Functions/FetchProperties";
import { useNavigate, useLocation } from "react-router-dom";

// Add a helper for location data caching
const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour in milliseconds

const Hero = ({ isPropertiesPage = false, initialLocation = "" }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [guests, setGuests] = useState({ adults: 2, children: 0 });
    const [showGuestDropdown, setShowGuestDropdown] = useState(false);
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: "selection",
        },
    ]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [locations, setLocations] = useState([]);
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [loading, setLoading] = useState(true);
    const [propertyCounts, setPropertyCounts] = useState({
        total: 0,
        filtered: 0,
        currentLocation: ""
    });

    // Enhanced loading states for better UI feedback
    const [loadingLocations, setLoadingLocations] = useState(true);
    const [loadingCounts, setLoadingCounts] = useState(true);

    // Use a ref to track if this is the initial mount
    const [initialMount, setInitialMount] = useState(true);

    // Initialize selectedLocation from props
    useEffect(() => {
        if (initialLocation && isPropertiesPage) {
            setSelectedLocation(initialLocation);
        }
    }, [initialLocation, isPropertiesPage]);

    // Parse URL search parameters to get current location filter (only for properties page)
    useEffect(() => {
        if (!isPropertiesPage) return;
        
        const queryParams = new URLSearchParams(location.search);
        const locationParam = queryParams.get('location') || "";
        
        setPropertyCounts(prev => ({
            ...prev,
            currentLocation: locationParam // Set to empty when no location is specified
        }));
    }, [location.search, isPropertiesPage]);

    // Listen for custom event that signals filter clearing
    useEffect(() => {
        if (!isPropertiesPage) return;

        // Handler for the custom event
        const handleFilterCleared = () => {
            setPropertyCounts(prev => ({
                ...prev,
                currentLocation: ""
            }));
        };

        // Add event listener
        window.addEventListener('filtersCleared', handleFilterCleared);
        
        // Clean up
        return () => {
            window.removeEventListener('filtersCleared', handleFilterCleared);
        };
    }, [isPropertiesPage]);

    // Listen for custom events from VillaList
    useEffect(() => {
        if (!isPropertiesPage) return;

        // Handler for filter cleared event
        const handleFilterCleared = (event) => {
            const count = event.detail?.count || 0;
            setPropertyCounts(prev => ({
                ...prev,
                filtered: count,
                total: count,
                currentLocation: ""
            }));
        };

        // Handler for location filter changed event
        const handleLocationFilterChanged = (event) => {
            const { location, count } = event.detail;
            setPropertyCounts(prev => ({
                ...prev,
                filtered: count,
                currentLocation: location
            }));
            
            // Also update the selectedLocation in this component to keep the search form in sync
            setSelectedLocation(location);
        };

        // Handler for all filters applied event
        const handleFiltersApplied = (event) => {
            const { location, count, totalCount } = event.detail;
            setPropertyCounts(prev => ({
                ...prev,
                filtered: count,
                total: totalCount || prev.total,
                currentLocation: location
            }));
            
            // Also update the selectedLocation in this component to keep the search form in sync
            setSelectedLocation(location);
        };

        // Add event listeners
        window.addEventListener('filtersCleared', handleFilterCleared);
        window.addEventListener('locationFilterChanged', handleLocationFilterChanged);
        window.addEventListener('filtersApplied', handleFiltersApplied);
        
        // Clean up
        return () => {
            window.removeEventListener('filtersCleared', handleFilterCleared);
            window.removeEventListener('locationFilterChanged', handleLocationFilterChanged);
            window.removeEventListener('filtersApplied', handleFiltersApplied);
        };
    }, [isPropertiesPage]);

    // Optimized data fetching with synchronization to URL params
    useEffect(() => {
        const fetchLocationsAndCounts = async () => {
            try {
                // Check if we have cached locations in sessionStorage
                const cachedData = sessionStorage.getItem('propertyLocationsData');
                let locationSet = new Set();
                let locationCounts = {};
                let propertiesArray = [];
                
                // If cache exists and is not expired, use it
                if (cachedData) {
                    const parsedCache = JSON.parse(cachedData);
                    const cacheAge = Date.now() - parsedCache.timestamp;
                    
                    if (cacheAge < CACHE_EXPIRY) {
                        // Use cached data
                        locationSet = new Set(parsedCache.locations);
                        locationCounts = parsedCache.locationCounts;
                        setLocations([...locationSet]);
                        setLoadingLocations(false);
                        
                        // Synchronize with URL parameters for property counts
                        if (isPropertiesPage) {
                            const totalCount = parsedCache.totalProperties;
                            
                            // Calculate filtered count based on current location (from URL or state)
                            let filteredCount = totalCount;
                            const locationToFilter = selectedLocation || propertyCounts.currentLocation;
                            
                            if (locationToFilter) {
                                filteredCount = locationCounts[locationToFilter] || 0;
                                // Ensure the property count title shows the correct location
                                setPropertyCounts(prev => ({
                                    ...prev,
                                    total: totalCount,
                                    filtered: filteredCount,
                                    currentLocation: locationToFilter
                                }));
                            } else {
                                setPropertyCounts(prev => ({
                                    ...prev,
                                    total: totalCount,
                                    filtered: totalCount,
                                    currentLocation: ""
                                }));
                            }
                            
                            setLoadingCounts(false);
                        }
                        
                        // If this is not properties page or it's not initial mount,
                        // we don't need to fetch the properties again
                        if (!isPropertiesPage || !initialMount) {
                            return;
                        }
                    }
                }
                
                // Otherwise, fetch fresh data
                setLoadingLocations(true);
                if (isPropertiesPage) {
                    setLoadingCounts(true);
                }
                
                const propertiesData = await getAllProperties();
                propertiesArray = propertiesToArray(propertiesData);
                
                // Extract unique locations
                locationSet = new Set();
                locationCounts = {};
                
                propertiesArray.forEach(prop => {
                    if (prop.location) {
                        let locationKey = prop.location;
                        
                        if (prop.location.toLowerCase().includes('pattaya') || 
                            prop.location.toLowerCase().includes('thailand')) {
                            locationKey = 'Pattaya, Thailand';
                        }
                        
                        locationSet.add(locationKey);
                        
                        // Count properties by location (only needed for properties page)
                        if (isPropertiesPage) {
                            locationCounts[locationKey] = (locationCounts[locationKey] || 0) + 1;
                        }
                    }
                });
                
                // Save to session storage for future use
                const cacheData = {
                    timestamp: Date.now(),
                    locations: [...locationSet].sort(),
                    locationCounts: locationCounts,
                    totalProperties: propertiesArray.length
                };
                sessionStorage.setItem('propertyLocationsData', JSON.stringify(cacheData));
                
                // Update state
                setLocations([...locationSet].sort());
                setLoadingLocations(false);
                
                // Update property counts (only for properties page)
                if (isPropertiesPage) {
                    // Get total property count
                    const totalCount = propertiesArray.length;
                    
                    // Calculate filtered count based on current location (from URL or state)
                    let filteredCount = totalCount;
                    const locationToFilter = selectedLocation || propertyCounts.currentLocation;
                    
                    if (locationToFilter) {
                        filteredCount = locationCounts[locationToFilter] || 0;
                        
                        setPropertyCounts({
                            total: totalCount,
                            filtered: filteredCount,
                            currentLocation: locationToFilter
                        });
                    } else {
                        setPropertyCounts({
                            total: totalCount,
                            filtered: totalCount,
                            currentLocation: ""
                        });
                    }
                    
                    setLoadingCounts(false);
                }
            } catch (error) {
                console.error("Error fetching locations:", error);
                setLoadingLocations(false);
                if (isPropertiesPage) {
                    setLoadingCounts(false);
                }
            }
        };
        
        fetchLocationsAndCounts();
        
        // After initial mount, update the state
        if (initialMount) {
            setInitialMount(false);
        }
    }, [isPropertiesPage, propertyCounts.currentLocation, initialMount, selectedLocation]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close date picker if clicking outside
            if (showDatePicker && !event.target.closest('.date-picker-dropdown') && 
                !event.target.closest('.search-item input') && !event.target.closest('.input-group-text')) {
                setShowDatePicker(false);
            }
            // Close guest dropdown if clicking outside
            if (showGuestDropdown && !event.target.closest('.guest-dropdown') && 
                !event.target.closest('.search-item:nth-child(3) input') && 
                !event.target.closest('.search-item:nth-child(3) .input-group-text')) {
                setShowGuestDropdown(false);
            }
            // Close location dropdown if clicking outside
            if (showLocationDropdown && !event.target.closest('.location-dropdown') && 
                !event.target.closest('.search-item:nth-child(1) input') && 
                !event.target.closest('.search-item:nth-child(1) .input-group-text')) {
                setShowLocationDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDatePicker, showGuestDropdown, showLocationDropdown]);

    const handleGuestChange = (e, type, operation) => {
        e.stopPropagation(); // Prevent event from bubbling up to parent elements
        setGuests((prev) => ({
            ...prev,
            [type]: operation === "increment" ? prev[type] + 1 : Math.max(0, prev[type] - 1),
        }));
    };

    const handleLocationSelect = (location) => {
        const locationInput = document.querySelector('.search-item:nth-child(1) input');
        if (locationInput) {
            locationInput.value = location;
        }
        setShowLocationDropdown(false);
        setTimeout(() => {
            setSelectedLocation(location);
        }, 0);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        let searchParams = new URLSearchParams();
        
        // Only include location parameter for filtering
        if (selectedLocation) {
            searchParams.append('location', selectedLocation);
        }
        
        // Navigate to properties page with only location parameter
        navigate(`/properties?${searchParams.toString()}`);
    };

    const handleDatePickerClick = (e) => {
        setShowDatePicker(!showDatePicker);
        
        if (!showDatePicker) {
            const inputRect = e.currentTarget.getBoundingClientRect();
            
            setTimeout(() => {
                const datePickerDropdown = document.querySelector('.date-picker-dropdown');
                if (datePickerDropdown) {
                    if (isPropertiesPage) {
                        datePickerDropdown.style.top = `${inputRect.bottom + 5}px`;
                        datePickerDropdown.style.left = `${inputRect.left}px`;
                        datePickerDropdown.style.width = '300px'; // Set explicit width for properties page
                    } else {
                        datePickerDropdown.style.position = 'absolute';
                        datePickerDropdown.style.top = '100%';
                        datePickerDropdown.style.left = '0';
                        datePickerDropdown.style.zIndex = '1000';
                        datePickerDropdown.style.width = '300px'; // Also set width for homepage
                    }
                }
            }, 0);
        }
    };

    const handleGuestDropdownClick = (e) => {
        e.preventDefault(); // Add preventDefault to completely stop the event
        e.stopPropagation(); // Also stop propagation here
        
        setShowGuestDropdown(!showGuestDropdown);
        
        if (!showGuestDropdown) {
            const inputRect = e.currentTarget.getBoundingClientRect();
            
            setTimeout(() => {
                const guestDropdown = document.querySelector('.guest-dropdown');
                if (guestDropdown) {
                    if (isPropertiesPage) {
                        // Use fixed positioning to prevent repositioning issues
                        guestDropdown.style.position = 'fixed';
                        guestDropdown.style.top = `${inputRect.bottom + 5}px`;
                        guestDropdown.style.left = `${inputRect.left}px`;
                        guestDropdown.style.width = `${inputRect.width}px`;
                        guestDropdown.style.zIndex = '2000'; // Higher z-index to prevent conflicts
                    } else {
                        guestDropdown.style.position = 'absolute';
                        guestDropdown.style.top = '100%';
                        guestDropdown.style.left = '0';
                        guestDropdown.style.width = '100%';
                        guestDropdown.style.zIndex = '1000';
                    }
                }
            }, 0);
        }
    };

    const handleLocationDropdownClick = (e) => {
        setShowLocationDropdown(!showLocationDropdown);
        
        if (!showLocationDropdown) {
            const inputRect = e.currentTarget.getBoundingClientRect();
            
            setTimeout(() => {
                const locationDropdown = document.querySelector('.location-dropdown');
                if (locationDropdown) {
                    if (isPropertiesPage) {
                        locationDropdown.style.top = `${inputRect.bottom + 5}px`;
                        locationDropdown.style.left = `${inputRect.left}px`;
                        locationDropdown.style.width = `${inputRect.width}px`;
                        // Add a minimum width to ensure dropdown isn't too narrow
                        locationDropdown.style.minWidth = '250px';
                    } else {
                        locationDropdown.style.position = 'absolute';
                        locationDropdown.style.top = '100%';
                        locationDropdown.style.left = '0';
                        locationDropdown.style.width = '100%';
                        locationDropdown.style.zIndex = '1000';
                    }
                }
            }, 0);
        }
    };

    const generateTitle = () => {
        if (!isPropertiesPage) {
            return "Luxury\nRedefined.";
        }
        
        if (loadingCounts) {
            return "Loading Properties...";
        }
        
        if (propertyCounts.currentLocation) {
            return `${propertyCounts.filtered} Properties in ${propertyCounts.currentLocation}`;
        } else {
            return `${propertyCounts.total} Luxury Properties`;
        }
    };

    const getLocationPlaceholder = () => {
        if (loadingLocations) return "Loading locations...";
        if (selectedLocation) return selectedLocation;
        return "All Locations"; 
    };

    return (
        <div>
            <section className="hero" style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80")', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="hero-overlay" />
                {isPropertiesPage ? (
                    <>
                        <div className="container search-container" style={{ paddingTop: "1.5rem", marginTop: "-25px" }}>
                            <div className="search-form-wrapper">
                                <form className="search-form" onSubmit={handleSearch}>
                                    <div className="row g-0">
                                        <div className="col-md-4 position-relative">
                                            <div className="search-item">
                                                <label>Location</label>
                                                <div 
                                                    className="input-group"
                                                    onClick={handleLocationDropdownClick}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <span className="input-group-text">
                                                        {loadingLocations ? (
                                                            <span className="location-spinner"></span>
                                                        ) : (
                                                            <i className="fas fa-map-marker-alt" />
                                                        )}
                                                    </span>
                                                    <input 
                                                        type="text" 
                                                        className="form-control" 
                                                        placeholder={getLocationPlaceholder()}
                                                        value={selectedLocation}
                                                        readOnly
                                                    />
                                                </div>
                                                {showLocationDropdown && (
                                                    <div className="location-dropdown location-dropdown-desktop">
                                                        {loadingLocations ? (
                                                            <div className="dropdown-item loading-item">
                                                                <div className="location-loading-spinner"></div>
                                                                Loading locations...
                                                            </div>
                                                        ) : locations.length > 0 ? (
                                                            <>
                                                                <div 
                                                                    className={`dropdown-item ${selectedLocation === "" ? "selected-location" : ""}`}
                                                                    onClick={() => handleLocationSelect("")}
                                                                >
                                                                    All Locations
                                                                </div>
                                                                {locations.map((location, index) => (
                                                                    <div 
                                                                        key={index} 
                                                                        className={`dropdown-item ${selectedLocation === location ? "selected-location" : ""}`}
                                                                        onClick={() => handleLocationSelect(location)}
                                                                    >
                                                                        {location}
                                                                    </div>
                                                                ))}
                                                            </>
                                                        ) : (
                                                            <div className="dropdown-item">No locations found</div> 
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-4 position-relative">
                                            <div className="search-item">
                                                <label>Check in - out</label>
                                                <div
                                                    className="input-group"
                                                    onClick={handleDatePickerClick}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <span className="input-group-text"><i className="far fa-calendar-alt" /></span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Select Dates"
                                                        value={`${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`}
                                                        readOnly
                                                    />
                                                </div>
                                                {showDatePicker && (
                                                    <div className="date-picker-dropdown">
                                                        <DateRange
                                                            editableDateInputs={true}
                                                            onChange={(item) => setDateRange([item.selection])}
                                                            moveRangeOnFirstSelection={false}
                                                            ranges={dateRange}
                                                            locale={enUS}
                                                            minDate={new Date()}
                                                            monthDisplayFormat="MMM yyyy"
                                                            weekdayDisplayFormat="E"
                                                            rangeColors={["var(--primary-color)"]}
                                                            className="compact-date-picker"
                                                            months={1}
                                                            showMonthAndYearPickers={true}
                                                            direction="vertical"
                                                        />
                                                        <div className="date-picker-actions">
                                                            <button 
                                                                onClick={() => setShowDatePicker(false)} 
                                                                className="btn btn-sm apply-dates-btn"
                                                            >
                                                                Apply
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-3 position-relative">
                                            <div className="search-item">
                                                <label>Guests</label>
                                                <div
                                                    className="input-group"
                                                    onClick={handleGuestDropdownClick}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <span className="input-group-text"><i className="fas fa-user" /></span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Select Guests"
                                                        value={`${guests.adults} Adults, ${guests.children} Children`}
                                                        readOnly
                                                    />
                                                </div>
                                                {showGuestDropdown && (
                                                    <div 
                                                        className="guest-dropdown" 
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        style={{ 
                                                            backgroundColor: 'var(--light-color)',
                                                            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
                                                            borderRadius: '0.5rem',
                                                            padding: '1rem'
                                                        }}
                                                    >
                                                        <div className="guest-item">
                                                            <span>Adults</span>
                                                            <div className="guest-controls">
                                                                <button type="button" onClick={(e) => handleGuestChange(e, "adults", "decrement")}>-</button>
                                                                <span>{guests.adults}</span>
                                                                <button type="button" onClick={(e) => handleGuestChange(e, "adults", "increment")}>+</button>
                                                            </div>
                                                        </div>
                                                        <div className="guest-item">
                                                            <span>Children</span>
                                                            <div className="guest-controls">
                                                                <button type="button" onClick={(e) => handleGuestChange(e, "children", "decrement")}>-</button>
                                                                <span>{guests.children}</span>
                                                                <button type="button" onClick={(e) => handleGuestChange(e, "children", "increment")}>+</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-1">
                                            <button className="btn search-btn" type="submit">
                                                <i className="fas fa-search" />
                                            </button>
                                        </div>
                                    </div>
                                </form>
                                <div className="glassmorphism-container">
                                    <div className="glass-content">
                                        <p>Find your perfect luxury retreat</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="container" style={{ 
                            position: "relative", 
                            marginTop: "25px", // Reduced from 50px
                            textAlign: "center",
                            zIndex: "1"
                        }}>
                            <div className="row justify-content-center">
                                <div className="col-lg-10 text-center">
                                    <h1 className="display-3 fw-bold property-count-title" style={{ 
                                        color: "white", 
                                        textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                                        overflowWrap: "break-word",
                                        hyphens: "auto"
                                    }}>
                                        {loadingCounts ? (
                                            <div className="count-loading">
                                                <span>Loading Properties</span>
                                                <span className="loading-dots">...</span>
                                            </div>
                                        ) : (
                                            generateTitle()
                                        )}
                                    </h1>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="container hero-content">
                            <div className="row justify-content-center text-center">
                                <div className="col-lg-8">
                                    <h1 className="display-1 fw-bold" style={{ whiteSpace: "pre-line" }}>{generateTitle()}</h1>
                                    <p className="lead">
                                        Discover exclusive luxury villas and cozy homestays with lsqstays. Experience
                                        unparalleled comfort in breathtaking locations across Maharashtra and Pattaya, Thailand.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="container search-container" style={{ marginTop: "2rem" }}>
                            <div className="search-form-wrapper">
                                <form className="search-form" onSubmit={handleSearch}>
                                    <div className="row g-0">
                                        <div className="col-md-4 position-relative">
                                            <div className="search-item">
                                                <label>Location</label>
                                                <div 
                                                    className="input-group"
                                                    onClick={handleLocationDropdownClick}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <span className="input-group-text">
                                                        {loadingLocations ? (
                                                            <span className="location-spinner"></span>
                                                        ) : (
                                                            <i className="fas fa-map-marker-alt" />
                                                        )}
                                                    </span>
                                                    <input 
                                                        type="text" 
                                                        className="form-control" 
                                                        placeholder={getLocationPlaceholder()}
                                                        value={selectedLocation}
                                                        readOnly
                                                    />
                                                </div>
                                                {showLocationDropdown && (
                                                    <div className="location-dropdown location-dropdown-desktop">
                                                        {loadingLocations ? (
                                                            <div className="dropdown-item loading-item">
                                                                <div className="location-loading-spinner"></div>
                                                                Loading locations...
                                                            </div>
                                                        ) : locations.length > 0 ? (
                                                            <>
                                                                <div 
                                                                    className={`dropdown-item ${selectedLocation === "" ? "selected-location" : ""}`}
                                                                    onClick={() => handleLocationSelect("")}
                                                                >
                                                                    All Locations
                                                                </div>
                                                                {locations.map((location, index) => (
                                                                    <div 
                                                                        key={index} 
                                                                        className={`dropdown-item ${selectedLocation === location ? "selected-location" : ""}`}
                                                                        onClick={() => handleLocationSelect(location)}
                                                                    >
                                                                        {location}
                                                                    </div>
                                                                ))}
                                                            </>
                                                        ) : (
                                                            <div className="dropdown-item">No locations found</div> 
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-4 position-relative">
                                            <div className="search-item">
                                                <label>Check in - out</label>
                                                <div
                                                    className="input-group"
                                                    onClick={handleDatePickerClick}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <span className="input-group-text"><i className="far fa-calendar-alt" /></span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Select Dates"
                                                        value={`${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`}
                                                        readOnly
                                                    />
                                                </div>
                                                {showDatePicker && (
                                                    <div className="date-picker-dropdown">
                                                        <DateRange
                                                            editableDateInputs={true}
                                                            onChange={(item) => setDateRange([item.selection])}
                                                            moveRangeOnFirstSelection={false}
                                                            ranges={dateRange}
                                                            locale={enUS}
                                                            minDate={new Date()}
                                                            monthDisplayFormat="MMM yyyy"
                                                            weekdayDisplayFormat="E"
                                                            rangeColors={["var(--primary-color)"]}
                                                            className="compact-date-picker"
                                                            months={1}
                                                            showMonthAndYearPickers={true}
                                                            direction="vertical"
                                                        />
                                                        <div className="date-picker-actions">
                                                            <button 
                                                                onClick={() => setShowDatePicker(false)} 
                                                                className="btn btn-sm apply-dates-btn"
                                                            >
                                                                Apply
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-3 position-relative">
                                            <div className="search-item">
                                                <label>Guests</label>
                                                <div
                                                    className="input-group"
                                                    onClick={handleGuestDropdownClick}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <span className="input-group-text"><i className="fas fa-user" /></span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Select Guests"
                                                        value={`${guests.adults} Adults, ${guests.children} Children`}
                                                        readOnly
                                                    />
                                                </div>
                                                {showGuestDropdown && (
                                                    <div 
                                                        className="guest-dropdown" 
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        style={{ 
                                                            backgroundColor: 'var(--light-color)',
                                                            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
                                                            borderRadius: '0.5rem',
                                                            padding: '1rem'
                                                        }}
                                                    >
                                                        <div className="guest-item">
                                                            <span>Adults</span>
                                                            <div className="guest-controls">
                                                                <button type="button" onClick={(e) => handleGuestChange(e, "adults", "decrement")}>-</button>
                                                                <span>{guests.adults}</span>
                                                                <button type="button" onClick={(e) => handleGuestChange(e, "adults", "increment")}>+</button>
                                                            </div>
                                                        </div>
                                                        <div className="guest-item">
                                                            <span>Children</span>
                                                            <div className="guest-controls">
                                                                <button type="button" onClick={(e) => handleGuestChange(e, "children", "decrement")}>-</button>
                                                                <span>{guests.children}</span>
                                                                <button type="button" onClick={(e) => handleGuestChange(e, "children", "increment")}>+</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-1">
                                            <button className="btn search-btn" type="submit">
                                                <i className="fas fa-search" />
                                            </button>
                                        </div>
                                    </div>
                                </form>
                                <div className="glassmorphism-container">
                                    <div className="glass-content">
                                        <p>Find your perfect luxury retreat</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

export default Hero;